import { motion, useInView } from 'motion/react';
import { useRef } from 'react';

import { cn } from '~/lib/utils';

interface ProductDescriptionSectionProps {
  text: string;
  className?: string;
  textClassName?: string;
}

/**
 * Centered body-text description block.
 * Defence-tech motion: controlled fade + subtle Y-shift, 600ms duration.
 */
export const ProductDescriptionSection = ({
  text,
  className,
  textClassName,
}: ProductDescriptionSectionProps) => {
  const ref = useRef<HTMLParagraphElement>(null);
  const isInView = useInView(ref, { once: true, amount: 0.4 });
  const words = text.split(' ');

  return (
    <section
      className={cn(
        'flex w-full flex-col items-center bg-black px-4 py-[80px] md:px-20',
        className
      )}
    >
      <p
        ref={ref}
        className={cn(
          "max-w-[560px] text-center font-['Manrope','Manrope_Fallback',sans-serif] text-[20px] font-normal leading-[1.4] text-white",
          textClassName
        )}
      >
        {words.map((word, i) => (
          <span key={i} className="inline-block align-bottom">
            <motion.span
              className="inline-block"
              style={{ willChange: 'transform, opacity' }}
              initial={{ y: '40%', opacity: 0 }}
              animate={isInView ? { y: '0%', opacity: 1 } : { y: '40%', opacity: 0 }}
              transition={{
                duration: 0.6,
                ease: [0.4, 0, 0.2, 1],
                delay: i * 0.03,
                opacity: {
                  duration: 0.9,
                  ease: [0.4, 0, 0.2, 1],
                  delay: i * 0.03,
                },
              }}
            >
              {word}
            </motion.span>
            {i < words.length - 1 && '\u00A0'}
          </span>
        ))}
      </p>
    </section>
  );
};
